import React, { useState, useEffect, useRef, useId } from 'react';
import { profileCountries, searchProfileCountries, ProfileCountry } from '../../data/profileCountries';

interface SearchableCountryDropdownProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  name?: string;
  autoComplete?: string;
  label?: string;
  required?: boolean;
  describedBy?: string;
  placeholder?: string;
  className?: string;
  error?: boolean;
  disabled?: boolean;
  showFlags?: boolean;
}

const countryFlag = (code: string) => /^[A-Z]{2}$/.test(code)
  ? Array.from(code, char => String.fromCodePoint(127397 + char.charCodeAt(0))).join('')
  : '';

export const SearchableCountryDropdown: React.FC<SearchableCountryDropdownProps> = ({
  value, onChange, id, name, autoComplete = 'off', label = 'Country', required = false, describedBy,
  placeholder = 'Search countries...', className = '', error = false, disabled = false, showFlags = false
}) => {
  const generatedId = useId();
  const inputId = id || `country-${generatedId}`;
  const listId = `${inputId}-options`;
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedCountry = profileCountries.find(country => country.name === value ||
    country.code === value || country.aliases.includes(value));
  const selectedFlag = showFlags && !isOpen && selectedCountry ? countryFlag(selectedCountry.code) : '';
  const options: ProfileCountry[] = searchProfileCountries(query);
  // Keep the exact persisted string selectable without adding it to the global list.
  if (value && !profileCountries.some(country => country.name === value) &&
      (!query || value.toLowerCase().includes(query.toLowerCase()))) {
    options.unshift({ code: 'saved', name: value, aliases: [] });
  }
  const dismiss = () => { setIsOpen(false); setQuery(''); setActiveIndex(-1); };
  useEffect(() => { dismiss(); }, [value, disabled]);
  useEffect(() => {
    const handleOutside = (event: PointerEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) dismiss();
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, []);
  useEffect(() => {
    if (activeIndex >= 0) document.getElementById(`${listId}-${activeIndex}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, listId]);
  const select = (country: ProfileCountry) => { onChange(country.name); dismiss(); };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex(index => options.length ? (event.key === 'ArrowDown'
        ? (index + 1) % options.length : (index <= 0 ? options.length - 1 : index - 1)) : -1);
    } else if (event.key === 'Enter' && isOpen) {
      event.preventDefault();
      if (activeIndex >= 0 && options[activeIndex]) select(options[activeIndex]);
    } else if (event.key === 'Escape' && isOpen) {
      event.preventDefault(); dismiss();
    }
  };
  return (
    <div className={`position-relative ${className}`} ref={dropdownRef}
      onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) dismiss(); }}>
      <div className="position-relative">
        <input ref={inputRef} id={inputId} name={name} type="text" role="combobox"
          aria-label={id ? undefined : label} aria-expanded={isOpen && !disabled}
          aria-controls={isOpen && !disabled ? listId : undefined} aria-autocomplete="list"
          aria-activedescendant={isOpen && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
          aria-invalid={error || undefined} aria-required={required} aria-describedby={describedBy}
          className={`form-control form-control-sm ${error ? 'is-invalid' : ''}`}
          style={{ paddingRight: value ? '4rem' : '2rem', paddingLeft: selectedFlag ? '2.5rem' : undefined }} placeholder={placeholder}
          value={isOpen ? query : value} onChange={event => { setQuery(event.target.value); setActiveIndex(-1); setIsOpen(true); }}
          onFocus={() => { if (!disabled) { setQuery(''); setActiveIndex(-1); setIsOpen(true); } }}
          onKeyDown={handleKeyDown} disabled={disabled} autoComplete={autoComplete} spellCheck={false} />
        {selectedFlag && <span aria-hidden="true" className="position-absolute top-50 translate-middle-y"
          style={{ left: '0.75rem', pointerEvents: 'none' }}>{selectedFlag}</span>}
        <i aria-hidden="true" className="bi bi-globe position-absolute end-0 top-50 translate-middle-y me-3 text-muted small" style={{ pointerEvents: 'none' }} />
        {value && <button type="button" className="btn btn-sm position-absolute top-50 translate-middle-y"
          style={{ right: '1.75rem' }} aria-label={`Clear ${label}`} disabled={disabled}
          onClick={() => { onChange(''); dismiss(); inputRef.current?.focus(); }}>×</button>}
      </div>
      {isOpen && !disabled && <div id={listId} role="listbox" aria-label={label}
        className="dropdown-menu show w-100" style={{ maxHeight: 'min(300px, 40vh)', overflowY: 'auto', minWidth: 0 }}>
        {options.length === 0 && <div className="px-3 py-2 text-muted" role="status">No countries found</div>}
        {options.map((country, index) => <div id={`${listId}-${index}`} key={country.code}
          role="option" aria-selected={country.name === value}
          className={`dropdown-item text-start ${index === activeIndex ? 'active' : ''}`}
          style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', cursor: 'pointer' }}
          onPointerDown={event => event.preventDefault()} onClick={() => select(country)}
          onPointerMove={() => setActiveIndex(index)}>
          <div className="fw-semibold">{showFlags && countryFlag(country.code) &&
            <span aria-hidden="true" className="me-2">{countryFlag(country.code)}</span>}{country.name}</div>
          <small className={index === activeIndex ? '' : 'text-muted'}>{country.code === 'saved' ? 'Current saved value' : country.code}</small>
        </div>)}
      </div>}
    </div>
  );
};
